import { Trans, useLingui } from "@lingui/react/macro";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import {
  Heading,
  HeadingWithEllipsis,
} from "@medoma/components/Heading/Heading";
import InvoicingBillIcon from "@medoma/components/icons/InvoicingBillIcon";
import { Panel } from "@medoma/components/Panel/Panel";
import type { IActivityCategory } from "@medoma/models/activities";
import type { IInvoicingCode } from "@medoma/models/invoicing";
import { patientTypeSchema, type IPatientId } from "@medoma/models/patients";
import { organizationUsesInvoicingCodes } from "@medoma/models/organizations";
import { useUserProfile } from "@/api/Users";
import { usePatient } from "@/api/Patients";
import { useInvoicingCodesFromPatientId } from "@/api/Invoicing";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import type { IDurationOption } from "@/forms/AddActivityForm/activityDurationModels";
import {
  postRegisterTemplateDefinitions,
  type IPostRegisterTemplate,
} from "./postRegisterTemplateDefinitions";
import styles from "./PostRegisterTemplates.module.scss";

export type IAppliedPostRegisterTemplate = {
  category: IActivityCategory;
  title: string;
  durationOption: IDurationOption;
  procedureCodes: string[];
  productCodes: string[];
};

const resolveCodes = (codes: string[], invoicingCodes: IInvoicingCode[]) =>
  codes
    .map((code) => invoicingCodes.find((entry) => entry.code === code))
    .filter((entry): entry is IInvoicingCode => entry !== undefined);

const resolveTemplate = (
  template: IPostRegisterTemplate,
  invoicingCodes: IInvoicingCode[],
) => {
  const procedureCodes = resolveCodes(template.procedureCodes, invoicingCodes);
  const productCodes = resolveCodes(template.productCodes, invoicingCodes);
  const allCodesResolved =
    procedureCodes.length === template.procedureCodes.length &&
    productCodes.length === template.productCodes.length;
  return allCodesResolved ? { template, procedureCodes, productCodes } : null;
};

export const PostRegisterTemplates = ({
  patientId,
  onApply,
}: {
  patientId: IPatientId;
  onApply: (applied: IAppliedPostRegisterTemplate) => void;
}) => {
  const { t } = useLingui();
  const { confirm } = useDialog();
  const { organization } = useUserProfile();
  const { data: patient } = usePatient(patientId);
  const { data: invoicingCodes } = useInvoicingCodesFromPatientId({
    patientId,
    codeType: null,
  });

  const showsInvoicingCodeTemplates =
    organizationUsesInvoicingCodes(organization) &&
    patient?.patientType === patientTypeSchema.enum.AdvancedHomeCare &&
    invoicingCodes !== undefined;

  if (!showsInvoicingCodeTemplates) {
    return null;
  }

  const availableTemplates = postRegisterTemplateDefinitions
    .filter((template) => template.patientType === patient.patientType)
    .filter((template) => template.organizations.includes(organization))
    .map((template) => resolveTemplate(template, invoicingCodes))
    .filter((resolved) => resolved !== null);

  if (availableTemplates.length === 0) {
    return null;
  }

  return (
    <section className={styles.container} aria-label={t`Mallar`}>
      <Heading level="h3" weight="medium">
        <Trans>Mallar</Trans>
      </Heading>
      <ul className={styles.list}>
        {availableTemplates.map(
          ({ template, procedureCodes, productCodes }) => {
            const title = t(template.title);
            const hasInvoicingCodes =
              procedureCodes.length > 0 || productCodes.length > 0;
            const applyTemplate = async () => {
              const applyConfirmed = await confirm({
                title: t`Använd mall`,
                message: t`Mallen fyller i fälten åt dig och skriver över eventuella värden. Vill du fortsätta?`,
              });
              if (!applyConfirmed) {
                return;
              }
              onApply({
                category: template.category,
                title,
                durationOption: template.durationOption,
                procedureCodes: procedureCodes.map(({ id }) => id),
                productCodes: productCodes.map(({ id }) => id),
              });
            };
            return (
              <Panel element="li" key={template.id}>
                <button
                  type="button"
                  className={styles.trigger}
                  onClick={applyTemplate}
                >
                  <div className={styles.header}>
                    <CategoryIcon category={template.category} size="small" />
                    <div className={styles.titleAndIcon}>
                      <HeadingWithEllipsis
                        level="h4"
                        size="base"
                        weight="regular"
                        maxWidth="unset"
                      >
                        {title}
                      </HeadingWithEllipsis>
                      {hasInvoicingCodes ? <InvoicingBillIcon /> : null}
                    </div>
                  </div>
                </button>
              </Panel>
            );
          },
        )}
      </ul>
    </section>
  );
};
